import { getRandomPartialColor } from '#lib/helper/color.js';
import { getMaxPrice, getMinPrice } from '#lib/helper/offer.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { offerStore } from '#lib/stores/offer.js';
import { sessionStore } from '#lib/stores/session.js';
import { now } from '#lib/stores/time.js';
import type {
	Booking,
	CustomerPackage,
	CustomerPackageComposition,
	CustomerPackageUsage,
	Package,
	PackageComposition,
	SavePackageRequest
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import { get } from 'svelte/store';

export function createNewPackage(): Package {
	return {
		id: generateUUID(),
		title: '',
		color: getRandomPartialColor(),
		textColor: '#ffffff',
		backgroundOverlayDisplay: false,
		logo: null,
		backgroundImage: null,
		description: '',
		price: 0,
		publishStart: null,
		publishEnd: null,
		limitPerCustomer: null,
		usageLimit: null,
		maxQuantityPerBooking: null,
		validityDays: 365,
		validityDates: null,
		createdAt: get(now),
		updatedAt: get(now),
		archivedAt: null,
		compositions: []
	};
}

export function toSavePackageRequest(pkg: Package, archived = false): SavePackageRequest {
	return {
		id: pkg.id,
		title: pkg.title,
		color: pkg.color,
		textColor: pkg.textColor,
		backgroundOverlayDisplay: pkg.backgroundOverlayDisplay,
		logo: pkg.logo,
		backgroundImage: pkg.backgroundImage,
		description: pkg.description,
		price: pkg.price,
		publishStart: pkg.publishStart
			? typeof pkg.publishStart === 'string'
				? pkg.publishStart
				: pkg.publishStart.toISO()
			: null,
		publishEnd: pkg.publishEnd
			? typeof pkg.publishEnd === 'string'
				? pkg.publishEnd
				: pkg.publishEnd.toISO()
			: null,
		limitPerCustomer: pkg.limitPerCustomer,
		usageLimit: pkg.usageLimit,
		maxQuantityPerBooking: pkg.maxQuantityPerBooking,
		validityDays: pkg.validityDays,
		validityDates: pkg.validityDates,
		archivedAt: archived || !!pkg.archivedAt,
		compositions: pkg.compositions.map((c) => ({
			id: c.id,
			quantity: c.quantity,
			details: c.details.map((d) => ({
				id: d.id,
				activityId: d.activityId,
				offerId: d.offerId,
				guestPriceId: d.guestPriceId
			}))
		}))
	};
}

export function getPackageCompositionsValueRange(compositions?: PackageComposition[]): {
	min: number;
	max: number;
} {
	const offers = get(offerStore);
	const sessions = get(sessionStore);

	const minValue = (compositions || []).reduce((acc, composition) => {
		let minItemPrice = 0;

		if (composition.details.length === 0) {
			// All offers
			const allOffersMin =
				offers.length > 0 ? Math.min(...offers.map((o) => getMinPrice(o))) : 0;
			minItemPrice = allOffersMin;
		} else {
			// Only restricted targets
			const possiblePrices: number[] = [];

			composition.details.forEach((t) => {
				if (t.activityId) {
					// Find offers linked to this activity via sessions
					const activityOffers = offers.filter(
						(o) =>
							!o.archivedAt &&
							o.sessionOrders.some((so) =>
								so.sessionIds.some((sid) => {
									const session = sessions.find((s) => s.id === sid);
									return session && session.parentId === t.activityId;
								})
							)
					);

					if (activityOffers.length > 0) {
						const min = Math.min(...activityOffers.map((o) => getMinPrice(o)));
						possiblePrices.push(min);
					}
				}

				if (t.offerId) {
					const offer = offers.find((o) => o.id === t.offerId);
					if (offer) {
						if (t.guestPriceId) {
							const gp = offer.guestPrices.find((p) => p.id === t.guestPriceId);
							if (gp) possiblePrices.push(gp.price);
						} else {
							// All prices of this offer
							possiblePrices.push(getMinPrice(offer));
						}
					}
				}
			});

			if (possiblePrices.length > 0) {
				minItemPrice = Math.min(...possiblePrices);
			}
		}

		return acc + composition.quantity * minItemPrice;
	}, 0);

	const maxValue = (compositions || []).reduce((acc, composition) => {
		let maxItemPrice = 0;
		if (composition.details.length === 0) {
			// All offers
			const allOffersMax =
				offers.length > 0 ? Math.max(...offers.map((o) => getMaxPrice(o))) : 0;
			maxItemPrice = allOffersMax;
		} else {
			const possiblePrices: number[] = [];
			composition.details.forEach((t) => {
				if (t.activityId) {
					const activityOffers = offers.filter(
						(o) =>
							!o.archivedAt &&
							o.sessionOrders.some((so) =>
								so.sessionIds.some((sid) => {
									const session = sessions.find((s) => s.id === sid);
									return session && session.parentId === t.activityId;
								})
							)
					);

					if (activityOffers.length > 0) {
						const max = Math.max(...activityOffers.map((o) => getMaxPrice(o)));
						possiblePrices.push(max);
					}
				}

				if (t.offerId) {
					const offer = offers.find((o) => o.id === t.offerId);
					if (offer) {
						if (t.guestPriceId) {
							const gp = offer.guestPrices.find((p) => p.id === t.guestPriceId);
							if (gp) possiblePrices.push(gp.price);
						} else {
							possiblePrices.push(getMaxPrice(offer));
						}
					}
				}
			});
			if (possiblePrices.length > 0) {
				maxItemPrice = Math.max(...possiblePrices);
			}
		}
		return acc + composition.quantity * maxItemPrice;
	}, 0);

	return { min: minValue, max: maxValue };
}

export function customerPackageCompositionAppliesTo(
	composition: CustomerPackageComposition,
	offerId: string,
	priceId: string | null
): boolean {
	if (composition.details.length === 0) return true;
	// Resolve the activityIds of the offer via sessionOrders → sessions
	const offer = get(offerStore).find((o) => o.id === offerId);
	const offerActivityIds = offer
		? offer.sessionOrders
				.flatMap((so) => so.sessionIds)
				.map((sId) => get(sessionStore).find((s) => s.id === sId)?.parentId)
				.filter((id): id is string => !!id)
		: [];
	return composition.details.some((d) => {
		if (
			d.activityId !== null &&
			offerActivityIds.length > 0 &&
			!offerActivityIds.includes(d.activityId)
		)
			return false;
		if (d.offerId !== null && d.offerId !== offerId) return false;
		if (d.guestPriceId !== null && d.guestPriceId !== priceId) return false;
		return true;
	});
}

/** True if this CP has net positive credits consumed on the given booking */
export function customerPackageCreditUsedForThisBooking(
	cp: CustomerPackage,
	booking: Pick<Booking, 'id'>
): boolean {
	const totalUsed = cp.compositions.reduce(
		(sum, c) =>
			sum +
			c.usages
				.filter((u) => u.bookingId === booking.id)
				.reduce((compSum, u) => compSum + u.quantity, 0),
		0
	);
	return totalUsed > 0;
}

/** True if at least one composition of this CP can apply to at least one offer/price in the booking */
export function customerPackageCanApplyToThisBooking(
	cp: CustomerPackage,
	booking: Booking
): boolean {
	if (cp.expiresOn && cp.expiresOn < get(now)) return false;
	return cp.compositions.some((composition) =>
		customerPackageCompositionCanApplyToThisBooking(composition, booking)
	);
}

export function customerPackageCompositionCanApplyToThisBooking(
	comp: CustomerPackageComposition,
	booking: Booking | undefined
): boolean {
	if (!booking || booking.offers.length === 0) return true;
	return booking.offers.some((orderOffer) => {
		if (orderOffer.prices.length === 0) {
			return customerPackageCompositionAppliesTo(comp, orderOffer.offerId, null);
		}
		return orderOffer.prices.some(
			(p) =>
				p.quantity > 0 &&
				customerPackageCompositionAppliesTo(comp, orderOffer.offerId, p.priceId)
		);
	});
}

export function createNewCustomerPackage(presetCustomerId?: string | null): CustomerPackage {
	return {
		id: '',
		customerId: presetCustomerId ?? '',
		packageId: null,
		packageName: null,
		price: 0,
		createdAt: get(now),
		expiresOn: null,
		orderId: null,
		payment: null,
		compositions: [],
		customer: null,
		archivedAt: null
	};
}

export function convertPackageToCustomerPackage(pkg: Package): CustomerPackage {
	return {
		id: generateUUID(),
		customerId: '',
		packageId: pkg.id,
		packageName: pkg.title,
		price: pkg.price,
		createdAt: get(now),
		expiresOn: pkg.validityDays ? get(now).plus({ days: pkg.validityDays }) : null,
		orderId: null,
		payment: null,
		customer: null,
		archivedAt: null,
		compositions: pkg.compositions.map((c) => ({
			id: c.id,
			initialQuantity: c.quantity,
			quantity: c.quantity,
			details: c.details,
			usages: []
		}))
	};
}

export function applyOptimisticPackageConsumption(
	cp: CustomerPackage,
	usages: CustomerPackageUsage[],
	usedAt?: DateTime
): CustomerPackage {
	const effectiveDate = usedAt ?? get(now);
	return {
		...cp,
		compositions: cp.compositions.map((c) => {
			const compUsages = usages.filter((u) => u.compositionId === c.id);
			const consumed = compUsages.reduce((sum, u) => sum + u.quantity, 0);
			if (consumed <= 0) return c;
			const newUsages: CustomerPackageUsage[] = compUsages.map((u) => ({
				id: u.id || generateUUID(),
				customerPackageId: cp.id,
				compositionId: c.id,
				quantity: u.quantity,
				offerId: u.offerId ?? null,
				guestPriceId: u.guestPriceId ?? null,
				bookingId: u.bookingId ?? null,
				usedAt: effectiveDate
			}));
			return {
				...c,
				quantity: c.quantity - consumed,
				usages: [...c.usages, ...newUsages]
			};
		})
	};
}

export function applyOptimisticPackageRecredit(
	cp: CustomerPackage,
	compositionId: number,
	quantity: number,
	usedAt?: DateTime
): CustomerPackage {
	const effectiveDate = usedAt ?? get(now);
	const optimisticUsage: CustomerPackageUsage = {
		id: generateUUID(),
		customerPackageId: cp.id,
		compositionId,
		quantity: -quantity,
		offerId: null,
		guestPriceId: null,
		bookingId: null,
		usedAt: effectiveDate
	};

	return {
		...cp,
		compositions: cp.compositions.map((c) =>
			c.id === compositionId
				? {
						...c,
						quantity: c.quantity + quantity,
						usages: [...c.usages, optimisticUsage]
					}
				: c
		)
	};
}

/** Returns the saved available credits for a package composition (accounting for any usages already saved for the given booking) */
export function getCustomerPackageCompositionSavedCredits(
	comp: CustomerPackageComposition,
	cpId: string,
	booking?: Booking
): number {
	const savedForBooking =
		booking?.packageUsages
			.filter((u) => u.customerPackageId === cpId && u.compositionId === comp.id)
			.reduce((sum, u) => sum + u.quantity, 0) ?? 0;
	return comp.quantity + savedForBooking;
}

/** Returns the effective available credits for a package composition accounting for in-progress usages or recredits */
export function getCustomerPackageCompositionEffectiveCredits(
	comp: CustomerPackageComposition,
	cpId: string,
	booking?: Booking,
	formUsages: CustomerPackageUsage[] = [],
	recreditQty: number = 0
): number {
	const savedCredits = getCustomerPackageCompositionSavedCredits(comp, cpId, booking);
	const pendingForComp = formUsages
		.filter((u) => u.compositionId === comp.id)
		.reduce((sum, u) => sum + u.quantity, 0);
	return Math.max(0, savedCredits - pendingForComp + recreditQty);
}

/** Returns the total saved available credits for a customer package */
export function getCustomerPackageSavedCredits(cp: CustomerPackage, booking?: Booking): number {
	return cp.compositions.reduce(
		(sum, c) => sum + getCustomerPackageCompositionSavedCredits(c, cp.id, booking),
		0
	);
}

/** Returns the total effective available credits for a customer package accounting for in-progress usages or recredits */
export function getCustomerPackageEffectiveCredits(
	cp: CustomerPackage,
	booking?: Booking,
	formUsages: CustomerPackageUsage[] = [],
	recreditCompId?: number,
	recreditQty: number = 0
): number {
	return cp.compositions.reduce((sum, c) => {
		const compRecredit = c.id === recreditCompId ? recreditQty : 0;
		return (
			sum +
			getCustomerPackageCompositionEffectiveCredits(
				c,
				cp.id,
				booking,
				formUsages,
				compRecredit
			)
		);
	}, 0);
}

/** Returns true if all saved credits of the customer package have been consumed */
export function isCustomerPackageCompleted(cp: CustomerPackage, booking?: Booking): boolean {
	return getCustomerPackageSavedCredits(cp, booking) === 0;
}

/** Returns true if the customer package has an expiration date and it is in the past */
export function isCustomerPackageExpired(
	cp: Pick<CustomerPackage, 'expiresOn'>,
	at?: DateTime
): boolean {
	const current = at ?? get(now);
	return Boolean(cp.expiresOn && cp.expiresOn < current);
}

/** Returns true if the customer package is past (either expired or fully consumed) */
export function isCustomerPackagePast(
	cp: CustomerPackage,
	at?: DateTime,
	booking?: Booking
): boolean {
	return isCustomerPackageExpired(cp, at) || isCustomerPackageCompleted(cp, booking);
}

/** Returns the suggested recredit quantity to restore the composition to its initial credits, or 0 if already full or exceeding */
export function getSuggestedRecreditQuantity(
	initialQuantity: number,
	currentQuantity: number
): number {
	const diff = initialQuantity - currentQuantity;
	return Math.max(0, diff);
}

/** Returns the percentage of package credits consumed (0 to 100), accounting for recredits exceeding initial quantity */
export function getCustomerPackageUsedPercent(totalQty: number, remainingQty: number): number {
	const effectiveTotal = Math.max(totalQty, remainingQty);
	if (effectiveTotal <= 0) return 0;
	const consumed = effectiveTotal - remainingQty;
	return Math.max(0, Math.min(100, (consumed / effectiveTotal) * 100));
}
